import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { getUnitsNaturalOrder } from "../../../../../../../core/data/Helpers";
import { Box, Divider, Grid, IconButton, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Select } from "Components";
import { Label, Loading } from "@gpstrackit/gtc-react-controls";
import { DatePicker } from "Components/uiControls/DatePicker";
import { reset, searchVehicleActivityDates, searchVehicleTrips, setUnitVehicleTrail, setTripsModeActive } from "../../redux/slice";
import moment from "moment";
import { VehicleTripsResume } from "./vehicleTripsResume";
import { VehicleTripsList } from "./vehicleTripsList";
import { VehicleTripsExport } from "./vehicleTripsExport";
import { useUnitInputs, useRealtimeUnits } from 'Core/hooks';

const EmptyVehicleActivityDates = () => {
  const { messages } = useIntl();
  return (
    <div style={{
      width: "100%",
      height: "40vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <Label subTitle>{messages["realtimemaps_vehicletrails_no_activity_dates"]}</Label>
    </div>
  );
};

const LoadingVehicleTrips = () => {
  return (
    <div style={{
      width: "100%",
      height: "40vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <Loading show={true} />
    </div>
  );
};

export const VehicleTripsMode = ({ mapRef }) => {
  const { messages } = useIntl();
  const dispatch = useDispatch();

  const loadingUnits = useSelector((state) => state.catalogsRedux.loadingUnits);
  const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label'] });
  const {
    vehicleActivityDates,
    loadVehicleActivityDates,
    vehicleTrips,
    loadVehicleTrips,
  } = useSelector((state) => state.vehicleTrailsSliceRedux);
  const unitToPrint = useSelector(state => state.realtimeMapUtilitiesvehiclesTrailRedux.unitToPrint);
  const [unit, setUnit] = useState(unitToPrint ? { label: unitToPrint?.label, id: unitToPrint?.id } : null);
  const { unitInputs } = useUnitInputs(unit?.id);
  const [selectedTime, setSelectedTime] = useState();
  const [currentUnits, setCurrentUnits] = useState([]);

  useEffect(() => {
    return () => {
      mapRef?.current?.hideMarkerModal();
      dispatch(setTripsModeActive(false));
      setSelectedTime(null);
      setUnit(null);
      setCurrentUnits([]);
      dispatch(reset({ vehicleTrips: null, vehicleActivityDates: null }));
    }
  }, []);

  useEffect(() => {
    if (unit?.id) {
      setSelectedTime(null);
      dispatch(searchVehicleActivityDates({ unitId: unit.id }));
    }
  }, [unit?.id]);

  useEffect(() => {
    if (units && units?.length > 0) {
      let unitList = [];
      units.map((unit) => {
        if (unit.id !== -1) unitList.push(unit);
      });
      setCurrentUnits(unitList);
    }
  }, [units]);

  useEffect(() => {
    if (selectedTime && unit?.id) {
      dispatch(searchVehicleTrips({
        unitId: unit.id,
        startDate: moment(selectedTime).startOf('day').toISOString(),
        endDate: moment(selectedTime).endOf('day').toISOString(),
      }));
    }
  }, [selectedTime, unit?.id]);

  useEffect(() => {
    if (vehicleActivityDates?.items[vehicleActivityDates?.items?.length - 1] && vehicleActivityDates?.total > 0) {
      setSelectedTime(new Date(vehicleActivityDates?.items[vehicleActivityDates?.items?.length - 1]));
    }
  }, [vehicleActivityDates?.items, vehicleActivityDates?.total]);

  const days = useMemo(() => vehicleActivityDates?.items?.map((d) => {
    return moment(d).format('YYYY-MM-DD');
  }), [vehicleActivityDates?.items]);
  const uniqueDays = [...new Set(days)];

  const currentDateIndex = useMemo(() => {
    if (!selectedTime || uniqueDays?.length === 0) return -1;
    return uniqueDays?.findIndex((d) =>
      moment(d).isSame(moment(selectedTime), 'day')
    );
  }, [selectedTime, uniqueDays]);

  const canGoPrev = currentDateIndex > 0;
  const canGoNext = currentDateIndex >= 0 && currentDateIndex < uniqueDays?.length - 1;
  const nextDate = uniqueDays[currentDateIndex + 1];
  const prevDate = uniqueDays[currentDateIndex - 1];

  return (
    <div data-testid="vehicle-trips">
      <Grid item sm={12} className="cnt-date-range-driver">
        <Select
          id="vehicles"
          value={unit}
          onChange={(event) => {
            dispatch(setUnitVehicleTrail(event));
            setUnit(event);
          }}
          label={messages["realtimemaps_vehicletrails_unit"]}
          options={getUnitsNaturalOrder(currentUnits)}
          textField="label"
          valueField="id"
          placeholder={messages["vehicleView_select"]}
          required
          requiredMessage={
            messages["realtimemaps_vehicletrails_vehicletrail_validateunit"]
          }
          disabled={loadingUnits}
        />
        {unit?.id &&
          !loadVehicleActivityDates &&
          (vehicleActivityDates?.total > 0 ? (
            <Box mt="10px" sx={{ display: "flex", alignItems: "center", gap: 0 }}>
              <DatePicker
                className="datepicker_container--slider"
                value={selectedTime}
                maxDate={new Date(vehicleActivityDates?.items[vehicleActivityDates?.items?.length - 1])}
                onChange={(newDate) => {
                  setSelectedTime(newDate);
                }}
                disabledDates={
                  vehicleActivityDates.items.length > 0 ? vehicleActivityDates.items : []
                }
                disabled={vehicleActivityDates.items.length > 0 ? false : true}
                inline={vehicleActivityDates.items.length > 0}
              />
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={messages["realtimemaps_vehicletrails_date_prev"] ?? "Fecha anterior"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (canGoPrev) {
                          setSelectedTime(moment(prevDate).startOf('day').toDate());
                        }
                      }}
                      disabled={!canGoPrev}
                      aria-label={messages["realtimemaps_vehicletrails_date_prev"] ?? "Fecha anterior"}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={messages["realtimemaps_vehicletrails_date_next"] ?? "Fecha siguiente"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (canGoNext) {
                          setSelectedTime(moment(nextDate).startOf('day').toDate());
                        }
                      }}
                      disabled={!canGoNext}
                      aria-label={messages["realtimemaps_vehicletrails_date_next"] ?? "Fecha siguiente"}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          ) : (
            <EmptyVehicleActivityDates />
          ))}

        {loadVehicleTrips && (
          <LoadingVehicleTrips />
        )}

        {!loadVehicleTrips && vehicleTrips?.total > 0 && (
          <Box mt="10px">
            <Divider mb="10px" />
            <VehicleTripsResume unit={unit} vehicleTrips={vehicleTrips} />
            <VehicleTripsExport vehicleTrips={vehicleTrips} unitInputs={unitInputs} />
            <Divider mb="10px" />
            <VehicleTripsList selectedTime={selectedTime} mapRef={mapRef} vehicleTrips={vehicleTrips} vehicleTimezone={units.find(u => u.id === unit?.id)?.timeZone?.name} />
          </Box>
        )}

      </Grid>
    </div>
  );
};
